import { CircularProgress, makeStyles } from '@material-ui/core';
import React, { Context, createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import IFormSubmitResult from 'src/interfaces/IFormSubmitResult';
import { FailedResponse, submitRequest } from 'src/utilities/submitForm';
import { IAppGroupDefinition, ISubAppDefinition } from '../interfaces/IAppDefinition';
import IYear from 'src/interfaces/IYear';

const useStyles = makeStyles({
    loadingContainer: {
        position: 'fixed',
        left: '0',
        top: '0',
        right: '0',
        bottom: '0',
        backgroundColor: '#00295244',
        transition: 'all 0.2s',
    },
    loading: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: '64px',
        height: '64px',
        transform: 'translate(-50%, -50%)',
    },
});

export interface GlobalContextData extends GlobalState {
    isMobile: boolean;
    loading: boolean;
    currentApp?: ISubAppDefinition | IAppGroupDefinition;
    setLoading(loading: boolean): void;
    getYearsList(success: (result: IYear[]) => void, failed: FailedResponse): void;
    setConsultingYear(year: number): void;
}
interface GlobalState {
    yearList: IYear[];
    loading: boolean;
    notifsCount: number;
    opacity: number;
    consultingYear: number | undefined;
}

const defaultState: GlobalState = {
    yearList: [],
    loading: false,
    notifsCount: 0,
    opacity: 1,
    consultingYear: undefined,
};

const GlobalContext: Context<GlobalContextData> = createContext<GlobalContextData>({
    ...defaultState,
    isMobile: false,
    loading: false,
    setLoading: () => {},
    currentApp: undefined,
    getYearsList: () => {},
    setConsultingYear: () => {},
});

interface PageTitleProviderProps {
    children: ReactNode;
}

const GlobalContextProvider = ({ children }: PageTitleProviderProps) => {
    const classes = useStyles();
    const [width, setWidth] = useState<number>(window.innerWidth);

    function handleWindowSizeChange() {
        setWidth(window.innerWidth);
    }

    useEffect(() => {
        window.addEventListener('resize', handleWindowSizeChange);

        return () => {
            window.removeEventListener('resize', handleWindowSizeChange);
        };
    }, []);

    const [state, setState] = useState<GlobalState>(defaultState);

    const setConsultingYear = useCallback((year: number) => {
        setState((s: GlobalState) => ({
            ...s,
            consultingYear: year,
        }));
    }, []);

    const getYearsList = useCallback((success: (result: IYear[]) => void, failed: FailedResponse) => {
        submitRequest(
            `/api/edp/reference-years/yearsList`,
            undefined,
            undefined,
            undefined,
            (result: IYear[]) => {
                setState((s: GlobalState) => ({
                    ...s,
                    yearList: result,
                }));
                success(result);
            },
            (result: IFormSubmitResult) => {
                setState((s: GlobalState) => ({
                    ...s,
                    yearList: [],
                }));
                failed(result);
            },
            'GET'
        );
    }, []);

    const setLoading = useCallback(
        (loading) => {
            if (loading) setState((s) => ({ ...s, opacity: 1, loading: true }));
            else {
                setState((s) => ({ ...s, opacity: 0 }));
                setTimeout(() => {
                    setState((s) => ({ ...s, loading: false }));
                }, 250);
            }
        },
        [setState]
    );

    const isMobile: boolean = width <= 768;
    const value = useMemo(
        () => ({
            ...state,
            isMobile,
            loading: state.loading,
            setLoading,
            getYearsList,
            setConsultingYear,
        }),
        [state, isMobile, setLoading, getYearsList, setConsultingYear]
    );

    return (
        <GlobalContext.Provider value={value}>
            <div style={{ transition: 'filter 0.2s', filter: `blur(${state.loading ? 3 : 0}px)`, minHeight: '100vh' }}>{children}</div>
            <div className={classes.loadingContainer} style={{ display: state.loading ? 'block' : 'none', opacity: state.opacity }}>
                <div className={classes.loading}>
                    <CircularProgress style={{ width: '64px', height: '64px' }} color='secondary' />
                </div>
            </div>
        </GlobalContext.Provider>
    );
};

export default GlobalContext;
export { GlobalContextProvider };
